// // src/apiServices/masterAssemblyApi.ts

// import axios from 'axios';
// import { MasterAssembly } from '../models/MasterAssembly';

// const BASE_URL = 'https://localhost:7131';
    
// export const masterAssemblyApi = {
//  getByOEMAndPartNumber: async (
//   oem: string,
//   componentPartNumber: string
// ): Promise<MasterAssembly> => {
//   const url = `${BASE_URL}/Parts/search-master/${oem}/${componentPartNumber}`;
//   console.log(' GET:', url);

//   const response = await axios.get(url);

//   if (!response.data || !response.data.data) {
//     console.warn('⚠️ No data in response:', response.data);
//     throw new Error("No part found");
//   }

//   return response.data.data;
// }
// }


// src/apiServices/masterAssemblyApi.ts
import axios from 'axios';
import { CatalogFigure, MasterAssembly, SvgFile } from '../models/MasterAssembly';
import api from '../api';
import type { ManualDto } from '../models/Manual';
import { AssemblyStatus } from '../store/UseAuthorStore';

// const BASE_URL = 'https://localhost:7131';
const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

type UploadSvgParams = {
  file: File;
  masterAssemblyId: number;  // required by API
  figNumber: string;         // required by API
  oem: string;               // required by API
  onUploadProgress?: (percent: number) => void;
};

type ApiResponse<T> = {
  statusCode: number;
  message?: string;
  data?: T;
};

type UpReviseRequest = {
  componentPartNumber: string;
  requestedBy: string;
};

type UpReviseUpdatedDto = {
  masterAssemblyId: number;
  componentPartNumber: string;
  currentVersionId: number;
  isFinalized: boolean;
};

type UpReviseData = {
  sourceMasterAssemblyId: number;
  newMasterAssemblyId: number;
  newVersion: number;
  manualsLinked: number;
  updatedDto: UpReviseUpdatedDto;
};

export type MasterWithManualsDto = {
  masterAssembly: MasterAssembly | null;
  manuals: ManualDto[];
};

export const masterAssemblyApi = {
  getMasterWithManualsByRole: async (
    oem: string,
    masterAssemblyId: number,
    role: string,
    lang?: string
  ): Promise<MasterWithManualsDto> => {
    const normalizedLang = String(lang || "").trim().toLowerCase();
    const query =
      normalizedLang.length > 0
        ? `?lang=${encodeURIComponent(normalizedLang)}`
        : "";
    const url = `/Parts/master-with-manuals/${encodeURIComponent(oem)}/${masterAssemblyId}/${encodeURIComponent(role)}${query}`;
    console.log(' GET:', `${BASE_URL}${url}`);

    const response = await api.get<ApiResponse<MasterWithManualsDto>>(url);
    const payload = response?.data;
    if (payload?.statusCode === 200 && payload.data) {
      return {
        masterAssembly: payload.data.masterAssembly ?? null,
        manuals: Array.isArray(payload.data.manuals) ? payload.data.manuals : [],
      };
    }

    throw new Error(payload?.message || 'Failed to load master assembly with manuals');
  },

  getByOEMAndPartNumber: async (
    oem: string,
    componentPartNumber: string
  ): Promise<MasterAssembly> => {
    const encodedOem = encodeURIComponent(oem);
    const encodedPartNumber = encodeURIComponent(componentPartNumber);
    const url = `${BASE_URL}/Parts/search-master/${encodedOem}/${encodedPartNumber}`;
    console.log(' GET:', url);

    const response = await axios.get(url);
    if (!response.data || !response.data.data) {
      console.warn('⚠️ No data in response:', response.data);
      throw new Error('No part found');
    }
    return response.data.data;
  },

  // ✅ matches SvgUploadController.UploadSvg form keys exactly
  uploadSvg: async ({
    file,
    masterAssemblyId,
    figNumber,
    oem,
    onUploadProgress,
  }: UploadSvgParams): Promise<SvgFile> => {
    const url = `${BASE_URL}/api/SvgUpload/upload`;
    console.log(' POST(upload):', url);

    const form = new FormData();
    form.append('svgFile', file);                      // <-- IFormFile svgFile
    form.append('masterAssemblyId', String(masterAssemblyId));
    form.append('figNumber', figNumber);
    form.append('oem', oem);

    const resp = await axios.post(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (!onUploadProgress || !evt.total) return;
        const percent = Math.round((evt.loaded * 100) / evt.total);
        onUploadProgress(percent);
      },
    });

    // Your controller returns ApiResponse; normalize to SvgFile
    const raw = resp?.data ?? {};
    const data = raw.data ?? raw; // supports { data: {...} } or plain

    const svg: SvgFile = {
       svgId: Number(data.svgId ?? data.id ?? 0),
  folderPath: String(data.filePath ?? data.FilePath ?? data.path ?? data.url ?? ''),
  fileName: String(data.fileName ?? data.FileName ?? data.name ?? file.name),
    };
    return svg;
  },

validateFigureBom: async (oem: string, figure: CatalogFigure) => {
  const url = `${BASE_URL}/Parts/figure-bom-check/${encodeURIComponent(oem)}`;

  console.log("PUT(validate):", url, figure);

  const resp = await axios.put(url, figure, {
    validateStatus: () => true // don't throw for any status
  });

  console.log("API resp", resp.status, resp.data); // status will be 400
  return resp.data;
},


addMaster: async (
    oem: string,
    payload: MasterAssembly
  ) => {
    const url = `${BASE_URL}/Parts/add-master/${encodeURIComponent(oem)}`;
    console.log("POST(add-master):", url, payload);

    const resp = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true, // let caller handle
    });

    return resp.data;
  },


  updateMaster: async (
    oem: string,
    payload: MasterAssembly
  ) => {
    const url = `${BASE_URL}/Parts/update-master/${encodeURIComponent(oem)}`;
    console.log("PUT(update-master):", url, payload);

    const resp = await axios.put(url, payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true, // let caller handle
    });

    return resp.data;
  },

  // src/apiServices/masterAssemblyApi.ts (add to the exported object)
  updateMasterTranslations: async (oem: string, payload: any) => {
    const url = `${BASE_URL}/Parts/update-master-translations/${encodeURIComponent(oem)}`;
    console.log("PUT(update-master-translations):", url, payload);
    const resp = await axios.put(url, payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true, // handle all statuses in the caller
    });
    return resp.data;
  },

  upRevise: async (
    oem: string,
    payload: UpReviseRequest
  ): Promise<ApiResponse<UpReviseData>> => {
    const url = `${BASE_URL}/Parts/up-revise/${encodeURIComponent(oem)}`;
    console.log("POST(up-revise):", url, payload);

    const resp = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true, // let caller handle 4xx/5xx response payloads
    });

    return resp.data;
  },

  getAllAssemblyStatuses: async (oem: string): Promise<AssemblyStatus[]> => {
    const url = `${BASE_URL}/Parts/partStatus/${oem}`;
    console.log(' GET:', url);

    const response = await axios.get(url);
    if (!response.data || !response.data.data) {
      console.warn('⚠️ No status data in response:', response.data);
      throw new Error('No statuses found');
    }

    return response.data.data as AssemblyStatus[];
  },

};
